import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, RotateCw } from "lucide-react";
import { cn } from "../../../../utils/utils";

const MAX_RETRIES = 4;
const RETRY_BASE_DELAY_MS = 800;

const isStableLocalPreview = (value) =>
  typeof value === "string" &&
  (value.startsWith("blob:") || value.startsWith("data:"));

const appendCacheBuster = (value, nonce) => {
  if (!value || nonce <= 0 || isStableLocalPreview(value)) return value;

  try {
    const url = new URL(value, window.location.href);
    url.searchParams.set("_imgRetry", String(nonce));
    return url.toString();
  } catch {
    const separator = value.includes("?") ? "&" : "?";
    return `${value}${separator}_imgRetry=${nonce}`;
  }
};

const ReliableChatImage = ({
  src,
  previewSrc,
  alt = "",
  className,
  containerClassName,
  fallbackClassName,
  onClick,
  onResolved,
  loading = "lazy",
  draggable = false,
  style,
}) => {
  const [retryNonce, setRetryNonce] = useState(0);
  const [resolvedSrc, setResolvedSrc] = useState(() => {
    if (!src) return "";
    if (previewSrc && previewSrc !== src) return "";
    return src;
  });
  const [isRetrying, setIsRetrying] = useState(false);
  const [hasExhaustedRetries, setHasExhaustedRetries] = useState(false);
  const retryTimeoutRef = useRef(null);

  const actualSrc = useMemo(
    () => appendCacheBuster(src, retryNonce),
    [src, retryNonce],
  );
  const hasPreview = Boolean(previewSrc && previewSrc !== src);
  const displaySrc = resolvedSrc || previewSrc || actualSrc;
  const shouldShowFallback = !displaySrc || hasExhaustedRetries;

  useEffect(() => {
    setRetryNonce(0);
    setHasExhaustedRetries(false);
    setIsRetrying(false);
    setResolvedSrc(
      !src ? "" : previewSrc && previewSrc !== src ? "" : src,
    );
  }, [previewSrc, src]);

  useEffect(
    () => () => {
      if (retryTimeoutRef.current) {
        window.clearTimeout(retryTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!src || !hasPreview || resolvedSrc) return undefined;

    let isCancelled = false;
    const img = new Image();

    img.onload = () => {
      if (isCancelled) return;
      setResolvedSrc(src);
      setHasExhaustedRetries(false);
      setIsRetrying(false);
      onResolved?.(src);
    };

    img.onerror = () => {
      if (isCancelled) return;
      if (retryNonce >= MAX_RETRIES) {
        setHasExhaustedRetries(true);
        setIsRetrying(false);
        return;
      }
      const nextRetry = retryNonce + 1;
      setIsRetrying(true);
      retryTimeoutRef.current = window.setTimeout(() => {
        setRetryNonce(nextRetry);
      }, RETRY_BASE_DELAY_MS * nextRetry);
    };

    img.src = actualSrc;

    return () => {
      isCancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [actualSrc, hasPreview, onResolved, resolvedSrc, retryNonce, src]);

  const handleVisibleImageLoad = () => {
    if (!src || hasPreview) return;
    setResolvedSrc(src);
    setHasExhaustedRetries(false);
    setIsRetrying(false);
    onResolved?.(src);
  };

  const handleVisibleImageError = () => {
    if (!src || hasPreview) return;
    if (retryNonce >= MAX_RETRIES) {
      setHasExhaustedRetries(true);
      setIsRetrying(false);
      return;
    }

    const nextRetry = retryNonce + 1;
    setIsRetrying(true);
    retryTimeoutRef.current = window.setTimeout(() => {
      setRetryNonce(nextRetry);
      setResolvedSrc("");
    }, RETRY_BASE_DELAY_MS * nextRetry);
  };

  const handleManualRetry = (event) => {
    event.stopPropagation();
    if (retryTimeoutRef.current) {
      window.clearTimeout(retryTimeoutRef.current);
    }
    setHasExhaustedRetries(false);
    setIsRetrying(false);
    setResolvedSrc(hasPreview ? "" : src || "");
    setRetryNonce((value) => value + 1);
  };

  return (
    <div
      className={cn(
        "relative overflow-hidden bg-gray-100",
        onClick && "cursor-pointer",
        containerClassName,
      )}
      onClick={onClick}
    >
      {displaySrc && (
        <img
          key={displaySrc}
          src={displaySrc}
          alt={alt}
          className={className}
          style={style}
          loading={loading}
          draggable={draggable}
          onLoad={handleVisibleImageLoad}
          onError={handleVisibleImageError}
        />
      )}

      {!displaySrc && !shouldShowFallback && (
        <div className={cn("animate-pulse bg-gray-100", className)} />
      )}

      {shouldShowFallback && (
        <button
          type="button"
          onClick={handleManualRetry}
          className={cn(
            "absolute inset-0 flex flex-col items-center justify-center gap-1 bg-gray-100/95 px-3 text-gray-500",
            fallbackClassName,
          )}
        >
          <AlertCircle className="h-5 w-5" />
          <span className="text-xs font-medium">Anh chua tai duoc</span>
          <span className="inline-flex items-center gap-1 text-xs text-blue-600">
            <RotateCw className="h-3.5 w-3.5" />
            Thu lai
          </span>
        </button>
      )}

      {!shouldShowFallback && isRetrying && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-black/55 px-2 py-1 text-center text-[11px] text-white">
          Dang thu tai lai...
        </div>
      )}
    </div>
  );
};

export default ReliableChatImage;
